import { useQuery } from '@tanstack/react-query';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
} from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import type { AssignmentShift } from '../../../../../js/api';
import {
  BasicFacility,
  ConnectedFacilityPublic,
  ProviderAvailabilityPublicService,
  ShiftRateTypesEnum,
  ShiftTypesEnum,
} from '../../../../../js/api';
import { BaseCalendar } from '../../../components/base-calendar';
import { Button } from '../../../components/button';
import { Checkbox } from '../../../components/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { Field, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { getShiftTypeButtonColor } from '../../../utils/globalStyles';
import { DEFAULT_SHIFT_SETTINGS, getShiftTypeDefaults } from '../../../utils/shiftTimes';
import { SHIFT_TYPE_ICONS, SHIFT_TYPE_LABELS, reactSelectReset } from '../../../utils/utils';
import type { NewShiftValues, ShiftEditValues } from './ShiftsTab';

type FacilityOption = {
  value: string;
  label: string;
  facility: BasicFacility;
};

interface ShiftEditModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: ShiftEditValues | NewShiftValues) => void;
  mode: 'edit' | 'add';
  shift?: AssignmentShift;
  assignmentFacility: { id: string; name: string };
  connectedFacility: ConnectedFacilityPublic | null;
  facilityOptions: FacilityOption[];
  providerToken?: string;
  /** First shift date on the assignment — calendar opens to this month in add mode */
  firstShiftDate?: string;
  /** When re-editing a shift that already has staged changes */
  pendingEdit?: ShiftEditValues;
  /** When editing an already-staged addition */
  existingAddition?: NewShiftValues;
}

const DATE_FORMAT_DISPLAY = 'EEEE, MMMM d, yyyy';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ShiftEditModal: React.FC<ShiftEditModalProps> = ({
  open,
  onClose,
  onApply,
  mode,
  shift,
  assignmentFacility,
  connectedFacility,
  facilityOptions,
  providerToken,
  firstShiftDate,
  pendingEdit,
  existingAddition,
}) => {
  // State
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [shiftType, setShiftType] = useState<ShiftTypesEnum | null>(null);
  const [onCall, setOnCall] = useState(false);
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('19:00');
  const [facilityId, setFacilityId] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [bonusAmount, setBonusAmount] = useState(0);
  const [holidayAmount, setHolidayAmount] = useState(0);

  // Snapshot of initial values for change detection
  const [initialValues, setInitialValues] = useState<{
    shiftType: ShiftTypesEnum | null;
    onCall: boolean;
    startTime: string;
    endTime: string;
    facilityId: string;
    bonusAmount: number;
    holidayAmount: number;
  } | null>(null);

  // Availability for add mode — always fetch fresh
  const [currentMonth, setCurrentMonth] = useState<Date>(() => startOfMonth(new Date()));

  const dateRange = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(addMonths(currentMonth, 1));
    return {
      startOn: format(start, 'yyyy-MM-dd'),
      endOn: format(end, 'yyyy-MM-dd'),
    };
  }, [currentMonth]);

  const availabilityEnabled = open && mode === 'add' && !!providerToken;
  const { data: availabilityData, isFetching: isFetchingAvailability } = useQuery({
    queryKey: ['shiftEditAvailability', providerToken, dateRange.startOn, dateRange.endOn],
    queryFn: async () => {
      if (!providerToken) throw new Error('Token required');
      return ProviderAvailabilityPublicService.providerAvailabilityPublicRetrieve({
        availabilityToken: providerToken,
        startOn: dateRange.startOn,
        endOn: dateRange.endOn,
      });
    },
    enabled: availabilityEnabled,
    cacheTime: 0,
    staleTime: 0,
  });
  const isLoadingAvailability = availabilityEnabled && isFetchingAvailability;
  const availableDates = (availabilityData?.available_dates as unknown as string[]) || [];

  // Disabled past dates
  const disabledDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfThisMonth = startOfMonth(today);
    if (today.getDate() <= 1) return [];
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return eachDayOfInterval({ start: startOfThisMonth, end: yesterday });
  }, []);

  // Available shift types from connection
  const availableShiftTypes = useMemo(() => {
    if (!connectedFacility?.shift_rate_types) return [];
    const isShiftType = (type: any): type is ShiftTypesEnum =>
      Object.values(ShiftTypesEnum).includes(type);
    return connectedFacility.shift_rate_types.reduce<ShiftTypesEnum[]>((acc, type) => {
      if (isShiftType(type)) acc.push(type);
      return acc;
    }, []);
  }, [connectedFacility]);

  const hasCallRate = useMemo(() => {
    if (!connectedFacility?.shift_rate_types) return true;
    return connectedFacility.shift_rate_types.includes(ShiftRateTypesEnum.CALL);
  }, [connectedFacility]);

  // Initialize from shift data (edit mode)
  /* eslint-disable sonarjs/cognitive-complexity */
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && shift) {
      // If there are already staged edits, load those; otherwise load from shift
      const pe = pendingEdit;
      const initType = pe
        ? (pe.shift_type as ShiftTypesEnum) || ShiftTypesEnum.DAY
        : ((shift as any).shift_type as ShiftTypesEnum) || ShiftTypesEnum.DAY;
      const initOnCall = pe
        ? pe.shift_rate_type === ShiftRateTypesEnum.CALL
        : shift.shift_rate_type === ShiftRateTypesEnum.CALL;
      const initStartTime = pe
        ? pe.start_time.substring(0, 5)
        : shift.start_time?.substring(0, 5) || '07:00';
      const initEndTime = pe
        ? pe.end_time.substring(0, 5)
        : shift.end_time?.substring(0, 5) || '19:00';
      const initFacilityId = pe ? pe.facility_id : shift.facility?.id || assignmentFacility.id;
      const initFacilityName = pe
        ? pe.facility_name
        : shift.facility?.name || assignmentFacility.name;
      const initBonus = pe ? pe.bonus_amount : shift.bonus_amount || 0;
      const initHoliday = pe ? pe.holiday_amount : shift.holiday_amount || 0;
      setShiftType(initType);
      setOnCall(initOnCall);
      setStartTime(initStartTime);
      setEndTime(initEndTime);
      setFacilityId(initFacilityId);
      setFacilityName(initFacilityName);
      setBonusAmount(initBonus);
      setHolidayAmount(initHoliday);
      setSelectedDate(null);
      // Always snapshot from the original shift for change detection
      setInitialValues({
        shiftType: ((shift as any).shift_type as ShiftTypesEnum) || ShiftTypesEnum.DAY,
        onCall: shift.shift_rate_type === ShiftRateTypesEnum.CALL,
        startTime: shift.start_time?.substring(0, 5) || '07:00',
        endTime: shift.end_time?.substring(0, 5) || '19:00',
        facilityId: shift.facility?.id || assignmentFacility.id,
        bonusAmount: shift.bonus_amount || 0,
        holidayAmount: shift.holiday_amount || 0,
      });
    } else if (mode === 'add' && existingAddition) {
      setShiftType((existingAddition.shift_type as ShiftTypesEnum) || ShiftTypesEnum.DAY);
      setOnCall(existingAddition.shift_rate_type === ShiftRateTypesEnum.CALL);
      setStartTime(existingAddition.start_time.substring(0, 5));
      setEndTime(existingAddition.end_time.substring(0, 5));
      setFacilityId(existingAddition.facility_id);
      setFacilityName(existingAddition.facility_name);
      setBonusAmount(existingAddition.bonus_amount);
      setHolidayAmount(existingAddition.holiday_amount);
      setSelectedDate(parseISO(existingAddition.start_on));
      setCurrentMonth(startOfMonth(parseISO(existingAddition.start_on)));
    } else if (mode === 'add') {
      setShiftType(null);
      setOnCall(false);
      setStartTime(connectedFacility?.shift_start_day?.substring(0, 5) || '07:00');
      setEndTime('19:00');
      setFacilityId(assignmentFacility.id);
      setFacilityName(assignmentFacility.name);
      setBonusAmount(0);
      setHolidayAmount(0);
      setSelectedDate(null);
      setCurrentMonth(
        firstShiftDate ? startOfMonth(parseISO(firstShiftDate)) : startOfMonth(new Date())
      );
    }
  }, [open, mode, shift, pendingEdit, existingAddition, assignmentFacility, connectedFacility]);

  // Recalculate times when shift type changes
  const applyShiftTypeDefaults = useCallback(
    (type: ShiftRateTypesEnum) => {
      if (!connectedFacility) return;
      const { startTime: nextStart, endTime: nextEnd } = getShiftTypeDefaults(
        type as unknown as ShiftTypesEnum,
        {
          shift_length: connectedFacility.shift_length || DEFAULT_SHIFT_SETTINGS.shift_length,
          shift_start_day:
            connectedFacility.shift_start_day || DEFAULT_SHIFT_SETTINGS.shift_start_day,
          shift_start_night:
            connectedFacility.shift_start_night || DEFAULT_SHIFT_SETTINGS.shift_start_night,
          shift_start_24h:
            connectedFacility.shift_start_24h || DEFAULT_SHIFT_SETTINGS.shift_start_24h,
        }
      );
      setStartTime(nextStart);
      setEndTime(nextEnd);
    },
    [connectedFacility]
  );

  const handleShiftTypeChange = (type: ShiftRateTypesEnum) => {
    setShiftType(type);
    applyShiftTypeDefaults(type);
  };

  const handleFacilityChange = (id: string) => {
    setFacilityId(id);
    const match = facilityOptions.find((o) => o.value === id);
    setFacilityName(match?.label || '');
  };

  // Compute end_on from shift type and start date
  const startOn =
    mode === 'edit'
      ? shift?.start_on || ''
      : selectedDate
        ? format(selectedDate, 'yyyy-MM-dd')
        : '';
  const endOn = useMemo(() => {
    if (!startOn) return '';
    if (shiftType === ShiftTypesEnum.NIGHT || shiftType === ShiftTypesEnum._24H) {
      return format(addDays(parseISO(startOn), 1), 'yyyy-MM-dd');
    }
    return startOn;
  }, [startOn, shiftType]);

  // Validation
  const isValid = useMemo(() => {
    if (!shiftType) return false;
    if (!startTime || !endTime) return false;
    if (!facilityId) return false;
    if (mode === 'add' && !selectedDate) return false;
    // Same-day: end time must be after start time
    return !(startOn === endOn && endTime <= startTime);
  }, [shiftType, startTime, endTime, facilityId, mode, selectedDate, startOn, endOn]);

  // Check if anything actually changed
  const hasChanges = useMemo(() => {
    if (mode === 'add') return true;
    if (!initialValues) return false;
    return (
      shiftType !== initialValues.shiftType ||
      onCall !== initialValues.onCall ||
      startTime !== initialValues.startTime ||
      endTime !== initialValues.endTime ||
      facilityId !== initialValues.facilityId ||
      bonusAmount !== initialValues.bonusAmount ||
      holidayAmount !== initialValues.holidayAmount
    );
  }, [
    mode,
    initialValues,
    shiftType,
    onCall,
    startTime,
    endTime,
    facilityId,
    bonusAmount,
    holidayAmount,
  ]);

  const handleApply = () => {
    const values: ShiftEditValues = {
      shift_type: shiftType || '',
      shift_rate_type: onCall ? 'call' : shiftType || '',
      start_time: startTime + ':00',
      end_time: endTime + ':00',
      end_on: endOn,
      facility_id: facilityId,
      facility_name: facilityName,
      bonus_amount: bonusAmount,
      holiday_amount: holidayAmount,
    };

    if (mode === 'add') {
      onApply({ ...values, start_on: startOn } as NewShiftValues);
    } else {
      onApply(values);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle className="tw:!text-base tw:!font-semibold tw:!mb-1 tw:p-2 tw:rounded-lg tw:bg-brand-green-0 tw:!text-white">
        {mode === 'edit' ? 'Edit Shift' : 'Add Shift'}
      </DialogTitle>
      <DialogBody className="tw:space-y-4">
        {/* Dates — read-only for edit, calendar for add */}
        {mode === 'edit' && startOn && (
          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Start Date</Label>
              <div className="tw:text-sm tw:font-medium tw:text-zinc-900">
                {format(parseISO(startOn), DATE_FORMAT_DISPLAY)}
              </div>
            </Field>
            <Field>
              <Label>End Date</Label>
              <div className="tw:text-sm tw:font-medium tw:text-zinc-900">
                {endOn ? format(parseISO(endOn), DATE_FORMAT_DISPLAY) : '--'}
              </div>
            </Field>
          </div>
        )}
        {mode === 'add' && (
          <Field>
            <Label>Select Date</Label>
            <div className="tw:relative">
              {isLoadingAvailability && (
                <div className="tw:absolute tw:inset-0 tw:bg-white/50 tw:z-10 tw:flex tw:items-center tw:justify-center">
                  <div className="tw:animate-spin tw:rounded-full tw:h-6 tw:w-6 tw:border-2 tw:border-zinc-300 tw:border-t-brand-green-0" />
                </div>
              )}
              <BaseCalendar
                availableDates={availableDates}
                selectedDates={selectedDate ? [selectedDate] : []}
                onSelectedDatesChange={(dates) => setSelectedDate(dates[dates.length - 1] || null)}
                numberOfMonths={1}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
                disabledDates={disabledDates}
                className="tw:mx-auto rdp-compact"
              />
            </div>
          </Field>
        )}

        {/* Shift Type Buttons */}
        {(mode === 'edit' || selectedDate) && (
          <Field>
            <Label>Shift Type</Label>
            <div className="tw:flex tw:gap-2 tw:flex-wrap">
              {availableShiftTypes
                .filter((t) => t !== ShiftRateTypesEnum.CALL)
                .map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`tw:px-3 tw:py-1.5 tw:!rounded-lg tw:border tw:text-sm tw:font-medium tw:cursor-pointer tw:transition-colors ${getShiftTypeButtonColor(type, shiftType === type)}`}
                    onClick={() => handleShiftTypeChange(type)}
                  >
                    <i className={`fas ${SHIFT_TYPE_ICONS[type]} tw:mr-1`} />
                    {SHIFT_TYPE_LABELS[type]}
                  </button>
                ))}
            </div>
            {hasCallRate && (
              <div className="tw:flex tw:items-center tw:gap-2 tw:mt-2">
                <Checkbox checked={onCall} onChange={(checked) => setOnCall(checked)} />
                <span className="tw:text-sm tw:text-zinc-600">On Call</span>
              </div>
            )}
          </Field>
        )}

        {/* Time Fields */}
        {shiftType && (
          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Start Time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </Field>
            <Field>
              <Label>End Time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </Field>
          </div>
        )}

        {/* Facility */}
        {shiftType && facilityOptions.length > 0 && (
          <Field>
            <Label>Facility</Label>
            <Select
              options={facilityOptions}
              value={facilityOptions.find((o) => o.value === facilityId) || null}
              onChange={(option) => {
                if (option) handleFacilityChange(option.value);
              }}
              placeholder="Change facility to..."
              className="tw:w-full"
              styles={reactSelectReset}
              isClearable={false}
            />
          </Field>
        )}

        {/* Bonus & Holiday */}
        {shiftType && (
          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Bonus ($)</Label>
              <Input
                type="number"
                min={0}
                value={bonusAmount || ''}
                onChange={(e) => setBonusAmount(parseInt(e.target.value, 10) || 0)}
                placeholder="0"
              />
            </Field>
            <Field>
              <Label>Holiday ($)</Label>
              <Input
                type="number"
                min={0}
                value={holidayAmount || ''}
                onChange={(e) => setHolidayAmount(parseInt(e.target.value, 10) || 0)}
                placeholder="0"
              />
            </Field>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button color="brandgreen0" disabled={!isValid || !hasChanges} onClick={handleApply}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
